import Image from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { memo } from "react";

export type ServicePlanCardProps = {
  title: string;
  image: string;
  price: string | number;
  description: string;
  highlight?: string;
  mainButton?: string;
  mainButtonLink?: string;
  secondaryButton?: string;
  secondaryButtonLink?: string;
  secondary?: boolean;
} & React.ComponentProps<"div">;

const ServicePlanCard = ({
  title,
  image,
  price,
  description,
  mainButton = "",
  mainButtonLink = "#",
  secondaryButton = "",
  secondaryButtonLink = "#",
  highlight = "",
  secondary = false,
  className,
}: ServicePlanCardProps) => {
  return (
    <div
      className={cn(
        "flex flex-col border rounded-3xl lg:rounded-xl text-center overflow-hidden lg:shadow-lg",
        {
          "border-blue-500 bg-blue-50": highlight === "accent",
          "border-[#C8D4F3] bg-white": highlight !== "accent",
        },
        className
      )}
    >
      <div className="flex flex-col p-6 flex-auto min-w-0">
        <div>
          <div className="grid grid-cols-1 gap-2 lg:gap-4">
            <div
              className={cn(
                "flex justify-center w-12 mx-auto lg:w-15 lg:mx-auto",
                {
                  "hidden md:flex": secondary,
                }
              )}
            >
              <Image alt={title} src={image} width={60} height={0} />
            </div>
            <div
              className={cn(
                "flex text-xl font-semibold lg:text-2xl justify-center font-bold lg:font-semibold text-gray-800",
                { "justify-start md:justify-center": secondary }
              )}
            >
              <h4>{title}</h4>
            </div>
          </div>
          <div
            className={cn(
              "mt-4 flex items-center justify-center text-2xl font-semibold lg:font-medium text-[#1F559E] lg:text-[2rem] whitespace-nowrap",
              { "hidden md:flex": secondary }
            )}
            dangerouslySetInnerHTML={{
              __html: price,
            }}
          />
        </div>
        <div
          className={cn(
            "pt-4 border-b-1 h-1 border-light-blue lg:border-[#e2edfc]",
            {
              "hidden md:block": secondary,
            }
          )}
        />
        <div
          className={cn(
            "mt-4 lg:mt-4.5 text-base leading-[160%] lg:text-sm lg:leading-[150%] lg:text-left flex text-grey-[#252f35]",
            {
              "line-clamp-2 text-left md:line-clamp-none mt-1 md:mt-4 leading-[130%] md:leading-[160%]": secondary,
            }
          )}
        >
          <p>{description}</p>
        </div>
        {!!secondary && (
          <div
            className={
              "mt-6 flex items-center justify-start text-2xl font-semibold lg:font-medium text-[#1F559E] lg:min-h-12 [&>span]:text-[32px] [&>span]:font-semibold [&>span]:lg:text-2xl [&>span]:lg:font-medium md:hidden"
            }
            dangerouslySetInnerHTML={{
              __html: price,
            }}
          />
        )}
        <div className="mt-auto grid grid-cols-1 gap-4 pt-6 empty:hidden">
          {!!mainButton && (
            <div className="flex justify-center">
              <Button
                asChild
                variant="primary"
                className="flex-auto min-w-0 h-[48]"
              >
                <Link href={mainButtonLink}>{mainButton}</Link>
              </Button>
            </div>
          )}
          {!!secondaryButton && (
            <div className="flex justify-center">
              <Button
                asChild
                variant="primary-outline"
                className="flex-auto min-w-0 lg:hidden h-[48]"
              >
                <Link href={secondaryButtonLink}>{secondaryButton}</Link>
              </Button>
              <Link
                href={secondaryButtonLink}
                className="text-[#2F80ED] font-medium text-[14px] flex-auto min-w-0 text-center hidden lg:block"
              >
                {secondaryButton}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default memo(ServicePlanCard);
